import { log } from "../../common/Logging"
import { Scale } from "./Scale"
import { sleepFor } from "./Timing"

export class VideoContainer {
    video: Phaser.Video
    videoImage: Phaser.Image
    destroyed = false

    constructor(video: Phaser.Video, videoImage: Phaser.Image) {
        this.video = video
        this.videoImage = videoImage
    }

    destroy = () => {
        if (!this.destroyed) {
            // Reset the play time before destroying the instance of video
            this.video.video.currentTime = 0
            this.video.destroy()
            this.videoImage.destroy()
            this.destroyed = true
        }
    }

    sendToBack = (game: Phaser.Game) => {
        game.world.sendToBack(this.videoImage)
        game.world.sendToBack(this.video)
    }
}

export class Videos {
    static add = async (
        game: Phaser.Game,
        asset: string,
        {
            x = 0,
            y = 0,
            scale = 1,
            volume = 1,
            onComplete,
            onPlay,
            onException,
            play = true,
            group,
            fillScreen = false,
            centerVertically = false,
            centerInLeftHalf = false,
            loop = false,
        }: {
            x?: number
            y?: number
            scale?: number
            volume?: number
            onComplete?: () => void
            onPlay?: () => void
            onException?: () => void
            play?: boolean
            group?: Phaser.Group
            fillScreen?: boolean
            centerVertically?: boolean
            centerInLeftHalf?: boolean
            loop?: boolean
        } = {},
    ): Promise<VideoContainer | null> => {
        let video
        try {
            video = addVideo(asset)
        } catch (e) {
            log.error("VIDEO EXCEPTION INITIAL for asset:", asset, e)
            // Try again after two seconds
            await sleepFor(2000)
            try {
                video = addVideo(asset)
            } catch (e) {
                log.error("VIDEO EXCEPTION TWO SECONDS for asset:", asset, e)
                // Try a third time after three additional seconds
                await sleepFor(3000)
                try {
                    video = addVideo(asset)
                } catch (e) {
                    log.error(
                        "VIDEO EXCEPTION FIVE SECONDS for asset:",
                        asset,
                        e,
                    )
                }
            }
        }
        if (video) {
            return video
        } else {
            // If still failing, do what we need to do onException
            if (onException) {
                onException()
                return null
            } else {
                throw new Error(
                    `No video after three tries and no onException fallback: ${asset}`,
                )
            }
        }

        function addVideo(asset: string): VideoContainer {
            const video = game.add.video(asset)
            video.video.autoplay = play

            let vidScale
            let realX = x
            let realY = y

            if (fillScreen) {
                const availableWidth = Scale.availableWidth()
                const availableHeight = Scale.availableHeight()
                const widthProportion = availableWidth / video.width
                const heightProportion = availableHeight / video.height
                if (widthProportion < heightProportion) {
                    vidScale = widthProportion
                    realY = (availableHeight - video.height * vidScale) / 2
                } else {
                    vidScale = heightProportion
                    realX = (availableWidth - video.width * vidScale) / 2
                }
            } else {
                vidScale = Scale.scaleAmount() * scale
            }

            if (centerVertically) {
                const availableHeight = Scale.availableHeight()
                realY = availableHeight / 2 - (video.height * vidScale) / 2
            }

            if (centerInLeftHalf) {
                const availableWidth = Scale.availableWidth()
                realX = availableWidth / 4 - (video.width * vidScale) / 2
            }

            const videoImage = video.addToWorld(
                realX,
                realY,
                0,
                0,
                vidScale,
                vidScale,
            )

            if (group) {
                group.add(videoImage)
            }

            // Bad Typescript definition!!!
            // Hopefully fixed when this is merged in and released: https://github.com/photonstorm/phaser-ce/pull/584
            // tslint:disable-next-line
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const anythingVideo = video as any
            anythingVideo.volume = volume

            if (onComplete) {
                video.onComplete.addOnce(onComplete)
            }

            if (onPlay) {
                video.onPlay.addOnce(onPlay)
            }

            if (play) {
                video.play(loop)
                // For applicable videos, shortcut users just see the first two seconds
                // Can comment this in if needed to move through the app quickly, but in general this reduces the fidelity of how close
                // dev is to prod
                // if (StudentStore.instance.useShortcuts() && onComplete) {
                //     setTimeout(() => {
                //         video.stop()
                //         onComplete()
                //     }, 2000)
                // }
            }

            return new VideoContainer(video, videoImage)
        }
    }
}
